/**
 * Role utility helpers.
 *
 * Source of truth: user.roles[] (from user_role_map via /users/me).
 * Fallback: user.role string (legacy single-role column) so nothing breaks
 * if the array is missing or empty (e.g. during a transitional deploy).
 */

import { Roles, ADMIN_ROLES } from "@/constants/roles";

interface UserRoleShape {
  role: string;
  roles?: string[];
}

const PRIMARY_ROLES = new Set([Roles.advisor, Roles.achiever, Roles.lead]);

/**
 * Return the names array from user.roles, or an empty array if missing.
 */
function getRoleNames(user: UserRoleShape): string[] {
  return user.roles && user.roles.length > 0 ? user.roles : [];
}

/**
 * Return true if the user has the given role.
 * Reads user.roles first; falls back to user.role.
 */
export function hasRole(
  user: UserRoleShape | null | undefined,
  roleName: string
): boolean {
  if (!user) return false;
  const names = getRoleNames(user);
  if (names.length > 0) return names.includes(roleName);
  // Fallback to legacy single-role string
  return user.role === roleName;
}

/**
 * Return the user's primary role string (advisor / achiever / lead).
 * Reads user.roles first; falls back to user.role.
 */
export function getPrimaryRole(
  user: UserRoleShape | null | undefined
): string {
  if (!user) return "";
  const names = getRoleNames(user);
  if (names.length > 0) {
    const primary = names.find((r) => PRIMARY_ROLES.has(r));
    if (primary) return primary;
  }
  // Fallback
  return user.role ?? "";
}

/**
 * Return true if the user has admin or super_admin role.
 */
export function isAdmin(user: UserRoleShape | null | undefined): boolean {
  if (!user) return false;
  const names = getRoleNames(user);
  if (names.length > 0) return names.some((r) => ADMIN_ROLES.has(r));
  // Fallback
  return ADMIN_ROLES.has(user.role);
}


/**
 * Return true if the user has the super_admin role.
 *
 * This — not isAdmin() — is the gate for the admin panel and its APIs. A
 * super_admin row in user_role_map is the single source of truth, granted only
 * by a developer inserting it directly; isAdmin() is deliberately broader
 * because it also matches the plain `admin` role.
 */
export function isSuperAdmin(user: UserRoleShape | null | undefined): boolean {
  return hasRole(user, Roles.super_admin);
}
/**
 * The `?role=` value on Expert signup links (`/signup?role=expert`). The
 * account role behind it is still `advisor` — that is what the backend and the
 * LinkedIn OAuth state expect — so only the visible URL says "expert".
 */
export const EXPERT_SIGNUP_ROLE_PARAM = "expert";

/**
 * The account role a signup link's `?role=` asks for: `role=expert` is an
 * Expert signup, and anything else — no param included — is an achiever
 * signup. The old `role=advisor` spelling is deliberately not recognised.
 */
export function signupRoleFromParam(param: string | null | undefined): "achiever" | "advisor" {
  return param === EXPERT_SIGNUP_ROLE_PARAM ? "advisor" : "achiever";
}
